import { Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, Output, SimpleChanges, OnChanges, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzRadioComponent, NzRadioGroupComponent } from 'ng-zorro-antd/radio';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { Subscription } from 'rxjs';
import { NzRadioGroupAccessibilityDirective } from '../../directives/nz-radio-group-accessibility.directive';

@Component({
  selector: 'app-symptom-assessment-survey',
  imports: [NzFormModule, TranslateModule, ReactiveFormsModule, NzIconModule, NzRadioGroupComponent, NzRadioComponent, NzRadioGroupAccessibilityDirective, NzCheckboxModule, NzTooltipModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './symptom-assessment-survey.component.html',
  styleUrl: './symptom-assessment-survey.component.scss',
})
export class SymptomAssessmentSurveyComponent implements OnInit, OnChanges {
  @Input() symptomSelected: any;
  @Input() questions: any[] = [];
  @Output() emitAnswers: EventEmitter<string[]> = new EventEmitter<string[]>();

  questionnaireForm!: FormGroup;
  private valueChangesSubscription?: Subscription;

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.initializeForm();
    this.subscribeToValueChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['questions'] && !changes['questions'].firstChange) {
      this.initializeForm();
      this.subscribeToValueChanges();
    }
  }

  public getFormControl(groupName: string, controlName: string): FormControl {
    const group = this.questionnaireForm.get(groupName) as FormGroup;
    if (!group) {
      console.error(`Group not found: ${groupName}`);
      return new FormControl();
    }
    const control = group.get(controlName) as FormControl;
    if (!control) {
      console.error(`Control not found: ${groupName} -> ${controlName}`);
      return new FormControl();
    }
    return control;
  }

  private initializeForm(): void {
    const formControls = this.questions.reduce(
      (controls, question, index) => {
        if (question.questions) {
          question.questions.forEach((subQuestion: { multiple: any; answers: any[] }, subIndex: any) => {
            if (subQuestion.multiple) {
              const groupControls: { [key: string]: FormControl } = {};
              subQuestion.answers.forEach((answer: any) => {
                groupControls[answer.code] = new FormControl(false);
              });
              controls[`question_${index}_${subIndex}`] = this.fb.group(groupControls);
            } else {
              controls[`question_${index}_${subIndex}`] = new FormControl(null);
            }
          });
        } else if (question.answers) {
          if (question.multiple) {
            const groupControls: { [key: string]: FormControl } = {};
            question.answers.forEach((answer: any) => {
              groupControls[answer.code] = new FormControl(false);
            });
            controls[`question_${index}`] = this.fb.group(groupControls);
          } else {
            controls[`question_${index}`] = new FormControl(null);
          }
        }
        return controls;
      },
      {} as { [key: string]: FormControl | FormGroup }
    );

    this.questionnaireForm = this.fb.group(formControls);
  }

  private subscribeToValueChanges(): void {
    if (this.valueChangesSubscription) {
      this.valueChangesSubscription.unsubscribe();
    }

    this.valueChangesSubscription = this.questionnaireForm.valueChanges.subscribe((formValues) => {
      const selectedCodes = this.mapSelectedCodes(formValues);
      this.emitAnswers.emit(selectedCodes);
    });
  }

  private mapSelectedCodes(formValues: any): string[] {
    const selectedCodes: string[] = [];

    this.questions.forEach((question, index) => {
      if (question.questions) {
        question.questions.forEach((subQuestion: { multiple: any }, subIndex: any) => {
          const formKey = `question_${index}_${subIndex}`;
          if (subQuestion.multiple) {
            const selectedForQuestion = Object.entries(formValues[formKey])
              .filter(([_, isSelected]) => isSelected)
              .map(([code, _]) => code);
            selectedCodes.push(...selectedForQuestion);
          } else {
            const selectedCode = formValues[formKey];
            if (selectedCode) {
              selectedCodes.push(selectedCode);
            }
          }
        });
      } else if (question.answers) {
        const formKey = `question_${index}`;
        if (question.multiple) {
          const selectedForQuestion = Object.entries(formValues[formKey])
            .filter(([_, isSelected]) => isSelected)
            .map(([code, _]) => code);
          selectedCodes.push(...selectedForQuestion);
        } else {
          const selectedCode = formValues[formKey];
          if (selectedCode) {
            selectedCodes.push(selectedCode);
          }
        }
      }
    });

    return selectedCodes;
  }

  ngOnDestroy(): void {
    if (this.valueChangesSubscription) {
      this.valueChangesSubscription.unsubscribe();
    }
  }
}
